import {
  Button,
  FormField,
  MultiSelect,
  NumberInput,
  TextArea,
  TextInput,
  useToast,
} from "@a2z/ui";
import { DrawerSheet } from "@/shared/components/dialogs/DrawerSheet";
import { useSlideScreen } from "@/shared/hooks/useSlideScreen";

import { useId, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import type { CategoryView, ProductView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import {
  ProductCreateImagePicker,
  type LocalProductImage,
} from "./ProductCreateImagePicker";
import { CameraIcon, InfoIcon, TagIcon } from "./product-edit-icons";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import type { ReactNode } from "react";
import { useCreateProductMutation } from "@/modules/products/services/useCreateProductMutation";
import { useCreateProductUploadIntentMutation } from "@/modules/products/services/useCreateProductUploadIntentMutation";
import { useUploadToSignedUrlMutation } from "@/shared/services/useUploadToSignedUrlMutation";
import { useUpdateProductImagesMutation } from "@/modules/products/services/useUpdateProductImagesMutation";
import {
  buildImagesPayload,
  uploadPendingImages,
} from "./product-images/helpers";
import type { ImageItem } from "./product-images/types";
import { Controller, useForm, useWatch, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  buildProductDetailsSchema,
  type ProductDetailsFormValues,
} from "./productDetailsSchema";

type Props = {
  locale: Locale;
  storeSlug: string;
  categories: CategoryView[];
  maxProductImageCount: number;
  onClose: (state: { created: boolean }) => void;
};

export function ProductCreateDrawer({
  locale,
  storeSlug,
  categories,
  maxProductImageCount,
  onClose,
}: Props) {
  const t = useTranslations();
  const toast = useToast();
  const isMobile = useIsMobile();
  const formId = useId();
  const [createdProduct, setCreatedProduct] = useState<ProductView | null>(null);
  const [localImages, setLocalImages] = useState<LocalProductImage[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const { mutateAsync: createProduct } = useCreateProductMutation();
  const { mutateAsync: createProductUploadIntent } =
    useCreateProductUploadIntentMutation();
  const { mutateAsync: uploadToSignedUrl } = useUploadToSignedUrlMutation();
  const { mutateAsync: updateProductImages } = useUpdateProductImagesMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ProductDetailsFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(buildProductDetailsSchema(t) as any) as Resolver<ProductDetailsFormValues>,
    defaultValues: {
      nameAr: "",
      nameEn: "",
      shortDescriptionAr: "",
      shortDescriptionEn: "",
      categoryIds: [],
      price: undefined,
      discountedPrice: undefined,
      stockCount: 1,
      forceOutOfStock: false,
      isActive: true,
    },
  });

  const handleClose = () => {
    onClose({ created: createdProduct !== null });
  };

  // Mobile: full-screen "New product" view (Trioty design) — slides in like the
  // detail/edit screens; the back button plays the reverse slide before closing.
  const createSlide = useSlideScreen<HTMLDivElement>(handleClose);
  const draftName = useWatch({ control, name: "nameEn" });

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError(null);
    setIsSaving(true);
    const trimOrNull = (s: string | null | undefined) => {
      const v = (s ?? "").trim();
      return v.length > 0 ? v : null;
    };
    try {
      // Idempotent on retry: if the product was already created but a later
      // step (image upload) failed, don't create a duplicate — reuse it.
      let product = createdProduct;
      if (!product) {
        product = await createProduct({
          storeSlug,
          nameAr: values.nameAr.trim(),
          nameEn: trimOrNull(values.nameEn ?? null),
          shortDescriptionAr: trimOrNull(values.shortDescriptionAr ?? null),
          shortDescriptionEn: trimOrNull(values.shortDescriptionEn ?? null),
          categoryIds: values.categoryIds ?? [],
          isActive: values.isActive,
          initialVariant: {
            price: values.price,
            discountedPrice: values.discountedPrice ?? null,
            stockCount: values.stockCount,
            forceOutOfStock: values.forceOutOfStock,
          },
        });
        setCreatedProduct(product);
      }

      if (localImages.length > 0) {
        const pending: ImageItem[] = localImages.map((img, index) => ({
          id: img.id,
          kind: "local",
          sortOrder: index,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          file: img.file,
          previewUrl: img.previewUrl,
        }));
        const uploaded = await uploadPendingImages({
          pendingImages: pending,
          productId: product.id,
          createUploadIntent: createProductUploadIntent,
          uploadToSignedUrl,
        });
        const imagesPayload = buildImagesPayload(pending, uploaded).filter(
          (
            image,
          ): image is {
            imageId: string;
            objectKey: string;
            sortOrder: number;
            useBgRemoved?: boolean;
          } => image !== null,
        );
        await updateProductImages({
          storeSlug,
          productId: product.id,
          images: imagesPayload,
        });
      }

      // Success — a toast is enough; close the drawer (parent refreshes list).
      toast.success(t(keys.seller.quickCreateSuccess));
      onClose({ created: true });
    } catch (caughtError) {
      // The product may already exist here (image step failed) — createdProduct
      // stays set so a resubmit retries only the upload, and closing still
      // reports the product as created.
      setGlobalError(
        caughtError instanceof Error ? caughtError.message : t(keys.errors.generic),
      );
    } finally {
      setIsSaving(false);
    }
  });

  const footerActions = (
    <div className="flex w-full items-center gap-2.5 lg:justify-end">
      <Button
        type="button"
        variant="outline"
        size="md"
        onClick={handleClose}
        className="max-lg:flex-1"
      >
        {t(keys.common.cancel)}
      </Button>
      <Button
        type="submit"
        form={formId}
        variant="secondary"
        size="md"
        disabled={isSaving}
        isLoading={isSaving}
        leadingIcon={<PlusIcon />}
        className="max-lg:flex-[1.5]"
      >
        {t(keys.products.createModalTitle)}
      </Button>
    </div>
  );

  // Field controls — bound to the shared `control`, composed into a flat form
  // (desktop, unchanged) or Trioty cards (mobile). Only one layout mounts at a
  // time; react-hook-form owns the values so switching preserves state.
  const imagePicker = (
    <ProductCreateImagePicker
      images={localImages}
      onChange={setLocalImages}
      maxImages={maxProductImageCount}
      disabled={isSaving}
    />
  );

  const nameArField = (
    <FormField
      label={t(keys.products.nameAr)}
      name="nameAr"
      required
      error={errors.nameAr?.message}
    >
      <Controller
        control={control}
        name="nameAr"
        render={({ field }) => (
          <TextInput
            name={field.name}
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t(keys.placeholders.productNameAr)}
            required
            hasError={Boolean(errors.nameAr)}
            dir="rtl"
            className="text-end"
          />
        )}
      />
    </FormField>
  );

  const nameEnField = (
    <FormField
      label={t(keys.products.nameEn)}
      name="nameEn"
      error={errors.nameEn?.message}
    >
      <Controller
        control={control}
        name="nameEn"
        render={({ field }) => (
          <TextInput
            name={field.name}
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t(keys.placeholders.productNameEn)}
            hasError={Boolean(errors.nameEn)}
          />
        )}
      />
    </FormField>
  );

  const descArField = (
    <FormField
      label={t(keys.products.shortDescriptionAr)}
      name="shortDescriptionAr"
      error={errors.shortDescriptionAr?.message}
    >
      <Controller
        control={control}
        name="shortDescriptionAr"
        render={({ field }) => (
          <TextArea
            name={field.name}
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t(keys.placeholders.productDescriptionAr)}
            hasError={Boolean(errors.shortDescriptionAr)}
            dir="rtl"
            rows={3}
          />
        )}
      />
    </FormField>
  );

  const descEnField = (
    <FormField
      label={t(keys.products.shortDescriptionEn)}
      name="shortDescriptionEn"
      error={errors.shortDescriptionEn?.message}
    >
      <Controller
        control={control}
        name="shortDescriptionEn"
        render={({ field }) => (
          <TextArea
            name={field.name}
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t(keys.placeholders.productDescriptionEn)}
            hasError={Boolean(errors.shortDescriptionEn)}
            rows={3}
          />
        )}
      />
    </FormField>
  );

  const categoriesField = (
    <FormField
      label={t(keys.products.categories)}
      name="categoryIds"
      helperText={t(keys.products.categoriesHelper)}
      error={errors.categoryIds?.message}
    >
      <Controller
        control={control}
        name="categoryIds"
        render={({ field }) => (
          <MultiSelect
            options={categories.map((c) => ({
              value: c.id,
              label: pickLocalized(locale, c.nameAr, c.nameEn),
            }))}
            value={field.value ?? []}
            onChange={field.onChange}
            aria-label={t(keys.products.categories)}
            placeholder={t(keys.products.categoriesPlaceholder)}
            searchPlaceholder={t(keys.common.search)}
            noResultsLabel={t(keys.search.noResults)}
          />
        )}
      />
    </FormField>
  );

  const priceField = (
    <FormField
      label={t(keys.common.price)}
      name="price"
      required
      error={errors.price?.message}
    >
      <Controller
        control={control}
        name="price"
        render={({ field }) => (
          <NumberInput
            name={field.name}
            allowDecimal
            value={
              typeof field.value === "number"
                ? String(field.value)
                : field.value ?? ""
            }
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t(keys.placeholders.price)}
            required
            hasError={Boolean(errors.price)}
          />
        )}
      />
    </FormField>
  );

  const discountField = (
    <FormField
      label={t(keys.common.discountedPrice)}
      name="discountedPrice"
      error={errors.discountedPrice?.message}
    >
      <Controller
        control={control}
        name="discountedPrice"
        render={({ field }) => (
          <NumberInput
            name={field.name}
            allowDecimal
            value={
              typeof field.value === "number"
                ? String(field.value)
                : field.value ?? ""
            }
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t(keys.placeholders.discountedPrice)}
            hasError={Boolean(errors.discountedPrice)}
          />
        )}
      />
    </FormField>
  );

  const stockField = (
    <FormField
      label={t(keys.products.stockCount)}
      name="stockCount"
      required
      error={errors.stockCount?.message}
    >
      <Controller
        control={control}
        name="stockCount"
        render={({ field }) => (
          <NumberInput
            name={field.name}
            value={
              typeof field.value === "number"
                ? String(field.value)
                : field.value ?? ""
            }
            onChange={field.onChange}
            onBlur={field.onBlur}
            required
            hasError={Boolean(errors.stockCount)}
          />
        )}
      />
    </FormField>
  );

  const globalErrorBlock = globalError ? (
    <div className="rounded-md border border-[color:var(--error-500)]/30 bg-[color:var(--error-500)]/8 px-3 py-2 text-[13px] text-[color:var(--error-500)]">
      {globalError}
    </div>
  ) : null;

  // Mobile: full-screen "New product" view (Trioty design). Same fields, same
  // submit + upload logic; only the container changes from a bottom-sheet.
  if (isMobile) {
    return (
      <div
        ref={createSlide.ref}
        className="fixed inset-0 z-[70] flex flex-col bg-[color:var(--canvas)] lg:hidden"
      >
        {/* Header: back · title + draft name · Draft badge */}
        <div
          className="flex shrink-0 items-center gap-3 border-b border-[color:var(--hair)] px-4 pb-3"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
        >
          <button
            type="button"
            onClick={createSlide.close}
            aria-label={t(keys.common.back)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
          >
            <BackIcon />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-extrabold leading-[1.15] tracking-[-0.02em] text-[color:var(--text-primary)]">
              {t(keys.products.createModalTitle)}
            </div>
            {draftName && draftName.trim() ? (
              <div className="mt-0.5 truncate text-[12px] font-semibold text-[color:var(--text-muted)]">
                {draftName}
              </div>
            ) : null}
          </div>
          <span className="shrink-0 rounded-full border border-[color:var(--hair)] bg-[color:var(--soft)] px-2.5 py-1 text-[10.5px] font-extrabold text-[color:var(--text-secondary)]">
            {t(keys.products.createDraftBadge)}
          </span>
        </div>

        {/* Scroll body — Trioty cards */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[116px] pt-4">
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-3.5">
            {globalErrorBlock}
            <CreateCard icon={<CameraIcon />} title={t(keys.products.imagesTitle)}>
              {imagePicker}
            </CreateCard>
            <CreateCard icon={<TagIcon />} title={t(keys.products.sectionPricing)}>
              <div className="grid grid-cols-2 gap-3">
                {priceField}
                {discountField}
              </div>
              {stockField}
            </CreateCard>
            <CreateCard icon={<InfoIcon />} title={t(keys.products.createStepDetails)}>
              {nameEnField}
              {nameArField}
              {descEnField}
              {descArField}
              {categoriesField}
            </CreateCard>
          </form>
        </div>

        {/* Bottom action bar */}
        <div
          className="absolute bottom-0 start-0 end-0 flex items-center gap-2.5 px-4 pt-6"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            background:
              "linear-gradient(to top, var(--canvas) 62%, color-mix(in srgb, var(--canvas) 0%, transparent))",
          }}
        >
          <button
            type="button"
            onClick={createSlide.close}
            className="flex flex-1 items-center justify-center gap-2 rounded-[15px] border border-[color:var(--card-br)] bg-[color:var(--soft)] py-[14px] text-[14.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft2)]"
          >
            {t(keys.common.cancel)}
          </button>
          <button
            type="submit"
            form={formId}
            disabled={isSaving}
            className="flex flex-[1.5] items-center justify-center gap-2 rounded-[15px] bg-[color:var(--accent-500)] py-[14px] text-[14.5px] font-semibold text-[color:var(--on-accent)] transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            <CheckIcon />
            {t(keys.products.createModalTitle)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <DrawerSheet
      isOpen
      onClose={handleClose}
      size="lg"
      icon={<BoxIcon />}
      title={t(keys.products.createModalTitle)}
      subtitle={t(keys.products.createModalSubtitle)}
      closeLabel={t(keys.common.cancel)}
      footerActions={footerActions}
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        {globalErrorBlock}
        {imagePicker}
        {nameArField}
        {nameEnField}
        {descArField}
        {descEnField}
        {categoriesField}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {priceField}
          {discountField}
        </div>
        {stockField}
      </form>
    </DrawerSheet>
  );
}

/** Trioty section card used inside the mobile create drawer body. */
function CreateCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-[18px] border border-[color:var(--hair)] bg-[color:var(--soft)] p-[15px]">
      <header className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-primary)]"
        >
          {icon}
        </span>
        <span className="text-[13.5px] font-bold leading-tight text-[color:var(--text-primary)]">
          {title}
        </span>
      </header>
      {children}
    </section>
  );
}

function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:-scale-x-100"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.1}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  );
}
